import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CounterRow = ({ label, hint, value, min, max, onChange, disabledDecrease, disabledIncrease }) => (
  <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0">
    <div>
      <div className="font-sofia font-medium text-black">{label}</div>
      {hint && <div className="text-xs font-sofia text-text-secondary mt-0.5">{hint}</div>}
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(value - 1)}
        disabled={disabledDecrease || value <= min}
        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30 hover:border-[#81736d]"
      >
        <Minus className="w-4 h-4" />
      </button>
      <span className="w-6 text-center font-sofia font-medium">{value}</span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        disabled={disabledIncrease || (max !== undefined && value >= max)}
        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30 hover:border-[#81736d]"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const GuestSelector = ({ value, maxTravelers, onChange }) => {
  const { t } = useTranslation();
  const { adults, children, babies, pets } = value;
  const travelers = adults + children;
  const canAddTraveler = travelers < maxTravelers;

  const update = (field, next) => {
    const minValues = { adults: 1, children: 0, babies: 0, pets: 0 };
    onChange({ ...value, [field]: Math.max(minValues[field], next) });
  };

  return (
    <div className="rounded-xl border border-gray-200 p-4 bg-white">
      <div className="mb-2">
        <h3 className="font-sofia font-semibold text-black">{t('booking.guestBreakdown.title')}</h3>
        <p className="text-sm font-sofia text-text-secondary">
          {t('booking.guestBreakdown.maxTravelers', { count: maxTravelers })}
        </p>
      </div>

      <CounterRow
        label={t('booking.guestBreakdown.adults')}
        hint={t('booking.guestBreakdown.adultsHint')}
        value={adults}
        min={1}
        onChange={(next) => update('adults', next)}
        disabledIncrease={!canAddTraveler}
      />
      <CounterRow
        label={t('booking.guestBreakdown.children')}
        hint={t('booking.guestBreakdown.childrenHint')}
        value={children}
        min={0}
        onChange={(next) => update('children', next)}
        disabledIncrease={!canAddTraveler}
      />
      <CounterRow
        label={t('booking.guestBreakdown.babies')}
        hint={t('booking.guestBreakdown.babiesHint')}
        value={babies}
        min={0}
        onChange={(next) => update('babies', next)}
      />
      <CounterRow
        label={t('booking.guestBreakdown.pets')}
        hint={t('booking.guestBreakdown.petsHint')}
        value={pets}
        min={0}
        onChange={(next) => update('pets', next)}
      />

      {travelers > maxTravelers && (
        <p className="mt-3 text-sm font-sofia text-red-600">
          {t('booking.guestBreakdown.travelersExceeded', { max: maxTravelers })}
        </p>
      )}
    </div>
  );
};

export default GuestSelector;

export const DEFAULT_GUESTS = {
  adults: 2,
  children: 0,
  babies: 0,
  pets: 0,
};

export const getTravelersCount = (guests) => (guests?.adults || 0) + (guests?.children || 0);
